/**
 * wishlist.js
 * Moxie — AE Wishlist (localStorage)
 */

const WISHLIST_KEY = 'mox_wishlist';
const WISHLIST_EVENT = 'wishlist:updated';
const WISHLIST_CARD_SECTION_ID = 'section-rendering-ae-product-card';
const WISHLIST_RENDER_CONCURRENCY = 4;
const WISHLIST_EAGER_CARD_COUNT = 4;
const WISHLIST_HIGH_PRIORITY_CARD_COUNT = 2;

function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveWishlist(list) {
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));

  // Moxie: detail vacío evita que listeners (GTM) crasheen al leer event.detail
  window.dispatchEvent(
    new CustomEvent(WISHLIST_EVENT, {
      detail: { count: list.length },
    })
  );
}

function getProductHandle(item) {
  if (item?.handle) return item.handle;

  if (item?.url) {
    return item.url.split('/products/')[1]?.split('?')[0] || '';
  }

  return '';
}

function htmlToElement(html) {
  const template = document.createElement('template');
  template.innerHTML = html.trim();

  return template.content.firstElementChild;
}

// ─── Wishlist Button ──────────────────────────────────────────────────────────

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this._productId = this.dataset.productId;
    this._button = this.querySelector('button');

    if (!this._button) return;

    const productCard = this.closest('ae-product-card');

    this._selectedVariantId =
      this.dataset.variantId ||
      productCard?.dataset.variantId ||
      null;

    this._onUpdate = () => this._updateState();

    this._onVariantUpdate = (event) => {
      const productId = event.detail?.data?.productId;
      const variantId = event.detail?.resource?.id;

      if (!variantId) return;

      if (
        productId &&
        String(productId) !== String(this._productId)
      ) {
        return;
      }

      this._selectedVariantId = String(variantId);
      this._updateState();
    };

    this._onSizeSelected = (event) => {
      const sizeButton = event.target.closest('.ae-card__size-btn');

      if (!sizeButton || !productCard?.contains(sizeButton)) {
        return;
      }

      const variantId = sizeButton.dataset.variantId;

      if (!variantId) return;

      this._selectedVariantId = String(variantId);
      this._updateState();
    };

    this._onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this._toggle();
    };

    window.addEventListener(
      'variant:update',
      this._onVariantUpdate,
      true
    );

    window.addEventListener(
      WISHLIST_EVENT,
      this._onUpdate
    );

    productCard?.addEventListener(
      'click',
      this._onSizeSelected,
      true
    );

    this._updateState();

    this.addEventListener(
      'click',
      this._onClick,
      true
    );
  }

  disconnectedCallback() {
    window.removeEventListener(
      'variant:update',
      this._onVariantUpdate,
      true
    );

    window.removeEventListener(
      WISHLIST_EVENT,
      this._onUpdate
    );

    const productCard = this.closest('ae-product-card');

    productCard?.removeEventListener(
      'click',
      this._onSizeSelected,
      true
    );

    this.removeEventListener(
      'click',
      this._onClick,
      true
    );
  }

  _getCurrentVariantId() {
    const productCard = this.closest('ae-product-card');

    return String(
      this._selectedVariantId ||
      this.dataset.variantId ||
      productCard?.dataset.variantId ||
      ''
    );
  }

  _toggle() {
    const list = getWishlist();
    const variantId = this._getCurrentVariantId();

    if (!variantId) return;

    const itemIndex = list.findIndex(
      (item) =>
        String(item.id) === String(this._productId) &&
        String(item.variant_id) === variantId
    );

    if (itemIndex > -1) {
      list.splice(itemIndex, 1);
    } else {
      list.push({
        id: this._productId,
        handle: this.dataset.handle,
        title: this.dataset.title,
        price: this.dataset.price,
        image: this.dataset.image,
        url: this.dataset.url,
        variant_id: variantId,
        quantity: 1,
      });
    }

    saveWishlist(list);
  }

  _updateState() {
    const variantId = this._getCurrentVariantId();

    const active = Boolean(
      variantId &&
      getWishlist().some(
        (item) =>
          String(item.id) === String(this._productId) &&
          String(item.variant_id) === variantId
      )
    );

    this._button.setAttribute(
      'aria-pressed',
      String(active)
    );

    this.classList.toggle(
      'wishlist-button--active',
      active
    );
  }
}

// ─── Wishlist Count ───────────────────────────────────────────────────────────

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this._onUpdate = () => this._update();

    window.addEventListener(
      WISHLIST_EVENT,
      this._onUpdate
    );

    this._update();
  }

  disconnectedCallback() {
    window.removeEventListener(
      WISHLIST_EVENT,
      this._onUpdate
    );
  }

  _update() {
    const count = getWishlist().length;

    this.textContent =
      count > 0 ? String(count) : '';

    this.hidden = count === 0;
  }
}

// ─── Wishlist Page ────────────────────────────────────────────────────────────

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this._cardCache = new Map();
    this._renderToken = 0;

    this._onUpdate = () => this.render();

    window.addEventListener(
      WISHLIST_EVENT,
      this._onUpdate
    );

    requestAnimationFrame(() =>
      this.render()
    );
  }

  disconnectedCallback() {
    window.removeEventListener(
      WISHLIST_EVENT,
      this._onUpdate
    );
  }

  async render() {
    const renderToken = ++this._renderToken;
    const list = getWishlist();

    const emptyEl = this.querySelector(
      '.wishlist-page__empty'
    );

    const gridEl = this.querySelector(
      '.wishlist-page__grid'
    );

    if (!gridEl) return;

    if (list.length === 0) {
      this.removeAttribute('data-loading');
      this.removeAttribute('data-ready');

      if (emptyEl) {
        emptyEl.hidden = false;
      }

      gridEl.replaceChildren();

      requestAnimationFrame(() =>
        this.setAttribute(
          'data-ready',
          ''
        )
      );

      return;
    }

    this.setAttribute(
      'data-loading',
      ''
    );

    this.removeAttribute(
      'data-ready'
    );

    if (emptyEl) {
      emptyEl.hidden = true;
    }

    const placeholders = list.map(() =>
      this._buildPlaceholder()
    );

    gridEl.replaceChildren(
      ...placeholders
    );

    requestAnimationFrame(() =>
      this.setAttribute(
        'data-ready',
        ''
      )
    );

    const renderedCount =
      await this._renderCardsProgressively(
        list,
        placeholders,
        renderToken
      );

    if (
      renderToken !==
      this._renderToken
    ) {
      return;
    }

    this.removeAttribute(
      'data-loading'
    );

    if (renderedCount === 0) {
      if (emptyEl) {
        emptyEl.hidden = false;
      }

      gridEl.replaceChildren();

      requestAnimationFrame(() =>
        this.setAttribute(
          'data-ready',
          ''
        )
      );
    }
  }

  async _fetchCardHtml(item) {
    const handle = getProductHandle(item);

    if (!handle) return null;

    const cacheKey =
      `${handle}:${item?.variant_id || ''}`;

    const cachedCard =
      this._cardCache.get(cacheKey);

    if (cachedCard) {
      return cachedCard;
    }

    const request = fetch(
      `/products/${encodeURIComponent(handle)}?section_id=${WISHLIST_CARD_SECTION_ID}`
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to render wishlist card for ${handle}`
          );
        }

        const html =
          await response.text();

        const doc =
          new DOMParser().parseFromString(
            html,
            'text/html'
          );

        return (
          doc.querySelector(
            'ae-product-card'
          )?.outerHTML || null
        );
      })
      .catch(() => null);

    this._cardCache.set(
      cacheKey,
      request
    );

    return request;
  }

  _buildPlaceholder() {
    const placeholder =
      document.createElement('div');

    placeholder.className =
      'wishlist-page__slot wishlist-page__slot--placeholder';

    placeholder.innerHTML = `
      <div class="wishlist-page__ph-media"></div>
      <div class="wishlist-page__ph-line wishlist-page__ph-line--short"></div>
      <div class="wishlist-page__ph-line"></div>
      <div class="wishlist-page__ph-line wishlist-page__ph-line--price"></div>
    `;

    return placeholder;
  }

  async _renderCardsProgressively(
    list,
    placeholders,
    renderToken
  ) {
    let nextIndex = 0;
    let renderedCount = 0;

    const workerCount =
      Math.min(
        WISHLIST_RENDER_CONCURRENCY,
        list.length
      );

    const workers = Array.from(
      {
        length: workerCount,
      },
      async () => {
        while (
          nextIndex <
          list.length
        ) {
          const currentIndex =
            nextIndex++;

          const placeholder =
            placeholders[currentIndex];

          const cardHtml =
            await this._fetchCardHtml(
              list[currentIndex]
            );

          if (
            renderToken !==
            this._renderToken
          ) {
            return;
          }

          if (
            !placeholder?.isConnected
          ) {
            continue;
          }

          if (!cardHtml) {
            placeholder.remove();
            continue;
          }

          const card =
            htmlToElement(cardHtml);

          if (
            !(card instanceof HTMLElement)
          ) {
            placeholder.remove();
            continue;
          }

          this._prepareCard(
            card,
            list[currentIndex],
            currentIndex
          );

          placeholder.replaceWith(
            card
          );

          requestAnimationFrame(() =>
            card.setAttribute(
              'data-ready',
              ''
            )
          );

          renderedCount += 1;
        }
      }
    );

    await Promise.all(workers);

    return renderedCount;
  }

  _prepareCard(card, item, index) {
    card.classList.add(
      'wishlist-page__slot'
    );

    const variantId =
      item?.variant_id
        ? String(item.variant_id)
        : '';

    if (variantId) {
      card.dataset.variantId =
        variantId;

      const wishlistButton =
        card.querySelector(
          'wishlist-button'
        );

      wishlistButton?.setAttribute(
        'data-variant-id',
        variantId
      );
    }

    this._prepareCardImages(
      card,
      index
    );
  }

  _prepareCardImages(card, index) {
    const media =
      card.querySelector(
        '.ae-card__media'
      );

    const images =
      Array.from(
        card.querySelectorAll(
          '.ae-card__img'
        )
      );

    if (images.length === 0) {
      media?.setAttribute(
        'data-media-ready',
        ''
      );

      return;
    }

    for (
      const [imageIndex, image]
        of images.entries()
    ) {
      image.decoding = 'async';

      if (
        imageIndex === 0 &&
        index <
          WISHLIST_EAGER_CARD_COUNT
      ) {
        image.loading = 'eager';

        image.setAttribute(
          'fetchpriority',
          index <
            WISHLIST_HIGH_PRIORITY_CARD_COUNT
            ? 'high'
            : 'auto'
        );
      } else {
        image.loading = 'lazy';

        image.setAttribute(
          'fetchpriority',
          'low'
        );
      }

      const markLoaded = () => {
        image.classList.add(
          'is-loaded'
        );

        if (imageIndex === 0) {
          media?.setAttribute(
            'data-media-ready',
            ''
          );
        }
      };

      if (
        image.complete &&
        image.naturalWidth > 0
      ) {
        markLoaded();
        continue;
      }

      image.addEventListener(
        'load',
        markLoaded,
        { once: true }
      );

      image.addEventListener(
        'error',
        () => {
          if (imageIndex === 0) {
            media?.setAttribute(
              'data-media-ready',
              ''
            );
          }
        },
        { once: true }
      );
    }
  }
}

// ─── Registration ─────────────────────────────────────────────────────────────

if (
  !customElements.get(
    'wishlist-button'
  )
) {
  customElements.define(
    'wishlist-button',
    WishlistButton
  );
}

if (
  !customElements.get(
    'wishlist-count'
  )
) {
  customElements.define(
    'wishlist-count',
    WishlistCount
  );
}

if (
  !customElements.get(
    'wishlist-page'
  )
) {
  customElements.define(
    'wishlist-page',
    WishlistPage
  );
}